import { DateTime } from 'luxon';
import { EMPTY_PLAN, pauseOn, sleepFor, templateDates, type PlanContext } from './planning.js';
import { occurrenceDates } from './recurrence.js';
import { MAX_MOVE_DAYS, type Item, type ItemException } from './schemas.js';
import {
  addDays,
  minutesToTime,
  timeToMinutes,
  toInstant,
  type LocalDate,
  type LocalTime,
} from './time.js';

export interface Occurrence {
  /** Stable identity of one occurrence: `${itemId}:${originDate}` (check-ins use it). */
  key: string;
  /** Own item, or an event imported from a connected calendar (read-only). */
  source: 'item' | 'event';
  /** Item id, or the external event id for `source: 'event'`. */
  itemId: string;
  /** Day it is shown on. */
  date: LocalDate;
  /** Day the rule produced it on (differs from `date` after a move to another day). */
  originDate: LocalDate;
  title: string;
  kind: Item['kind'];
  priority: Item['priority'];
  color: Item['color'];
  requiresCheck: boolean;
  notes: string | null;
  startTime: LocalTime;
  durationMin: number;
  /** Absolute start/end as ISO strings (UTC offset applied). */
  start: string;
  end: string;
  moved: boolean;
  /** Part of a repeating rule or a day template (edits can apply to one or all). */
  recurring: boolean;
  templateId?: string | null;
  goalId?: string | null;
  taskId?: string | null;
  /** Travel before the start / after the end (minutes; 0 = none, always 0 for events). */
  travelBeforeMin: number;
  travelAfterMin: number;
  /**
   * No time of day ("kadarkoli danes"): listed under "Danes še", not in the timeline or the
   * calendar; `start`–`end` span the whole day.
   */
  untimed?: boolean;
  /** How the time was set when it is not a clock time (for labels like "po Fitnes"). */
  anchor?: OccurrenceAnchor;
  /** Only for calendar events. */
  calendar?: { name: string; color: string };
  location?: string | null;
}

export type OccurrenceAnchor =
  | { type: 'anytime' }
  | { type: 'wake' | 'bed'; offsetMin: number }
  | {
      type: 'after';
      offsetMin: number;
      /** Title of the activity it follows. */
      title: string;
      /** That activity's occurrence (its check-in can trigger the reminder earlier). */
      occKey: string | null;
      /** Its end (ISO), or null on a day without it (fallback time). */
      anchorEnd: string | null;
    };

export type ScheduleItem = Pick<
  Item,
  | 'id'
  | 'title'
  | 'kind'
  | 'priority'
  | 'color'
  | 'requiresCheck'
  | 'notes'
  | 'startTime'
  | 'durationMin'
  | 'rrule'
  | 'startDate'
  | 'endDate'
> &
  Partial<
    Pick<
      Item,
      | 'templateId'
      | 'goalId'
      | 'taskId'
      | 'keepOnPause'
      | 'travelBeforeMin'
      | 'travelAfterMin'
      | 'seriesId'
      | 'anchorType'
      | 'anchorItemId'
      | 'anchorOffsetMin'
      | 'anchorFallback'
    >
  >;

export const occurrenceKey = (itemId: string, date: LocalDate): string => `${itemId}:${date}`;

interface Timing {
  startTime: LocalTime;
  untimed: boolean;
  anchor?: OccurrenceAnchor;
}

/** Dates on which an item occurs: by its rule, or (template blocks) wherever its template applies. */
export function itemDates(
  item: ScheduleItem,
  from: LocalDate,
  to: LocalDate,
  plan: PlanContext = EMPTY_PLAN,
): LocalDate[] {
  if (!item.templateId) return occurrenceDates(item, from, to);
  const lo = item.startDate > from ? item.startDate : from;
  const hi = item.endDate && item.endDate < to ? item.endDate : to;
  return lo > hi ? [] : templateDates(plan, item.templateId, lo, hi);
}

/**
 * Computes the schedule for a date range from items + exceptions (+ day templates).
 * Nothing is stored per day: occurrences are always derived, only exceptions and check-ins persist.
 */
export function expandOccurrences(input: {
  items: readonly ScheduleItem[];
  exceptions: readonly ItemException[];
  from: LocalDate;
  to: LocalDate;
  zone: string;
  plan?: PlanContext;
}): Occurrence[] {
  const exceptionByKey = new Map(
    input.exceptions.map((e) => [occurrenceKey(e.itemId, e.date), e] as const),
  );
  // Occurrences moved in from (or out to) a nearby day: look a little beyond the range.
  const crossDay = input.exceptions.some((e) => e.newDate);
  const from = crossDay ? addDays(input.from, -MAX_MOVE_DAYS) : input.from;
  const to = crossDay ? addDays(input.to, MAX_MOVE_DAYS) : input.to;
  const out: Occurrence[] = [];
  const plan = input.plan ?? EMPTY_PLAN;
  const seriesOf = new Map(input.items.map((i) => [i.id, i.seriesId ?? i.id] as const));

  /** One occurrence of `item` on `originDate`, with its time set by `resolve` (null = none). */
  const emit = (
    item: ScheduleItem,
    originDate: LocalDate,
    resolve: (date: LocalDate) => Timing | null,
  ) => {
    const key = occurrenceKey(item.id, originDate);
    const ex = exceptionByKey.get(key);
    if (ex?.action === 'skip') return;
    const moved = ex?.action === 'move';
    const date = moved && ex.newDate ? ex.newDate : originDate;
    if (date < input.from || date > input.to) return;
    // Holiday: repeating things pause (one-off plans and "tudi na dopustu" stay).
    const repeating = item.rrule !== null || Boolean(item.templateId);
    if (repeating && !item.keepOnPause && input.plan && pauseOn(input.plan, date)) return;
    // "Samo ta" with a new time pins this one to the clock.
    const timing: Timing | null =
      moved && ex.newTime ? { startTime: ex.newTime, untimed: false } : resolve(date);
    if (!timing) return;
    const durationMin = timing.untimed
      ? 0
      : moved && ex.newDurationMin != null
        ? ex.newDurationMin
        : item.durationMin;
    const start = toInstant(date, timing.untimed ? '00:00' : timing.startTime, input.zone);
    const end = timing.untimed ? start.plus({ days: 1 }) : start.plus({ minutes: durationMin });
    out.push({
      key,
      source: 'item',
      itemId: item.id,
      date,
      originDate,
      title: item.title,
      kind: item.kind,
      priority: item.priority,
      color: item.color,
      requiresCheck: item.requiresCheck,
      notes: item.notes,
      startTime: timing.untimed ? '00:00' : timing.startTime,
      durationMin,
      start: start.toUTC().toISO() ?? '',
      end: end.toUTC().toISO() ?? '',
      moved,
      recurring: item.rrule !== null || Boolean(item.templateId),
      templateId: item.templateId ?? null,
      goalId: item.goalId ?? null,
      taskId: item.taskId ?? null,
      // Items cached before travel times existed have none.
      travelBeforeMin: timing.untimed ? 0 : (item.travelBeforeMin ?? 0),
      travelAfterMin: timing.untimed ? 0 : (item.travelAfterMin ?? 0),
      ...(timing.untimed ? { untimed: true } : {}),
      ...(timing.anchor ? { anchor: timing.anchor } : {}),
    });
  };

  const wakeAt = (date: LocalDate, offset: number): Timing => ({
    startTime: minutesToTime(
      Math.min(24 * 60 - 1, timeToMinutes(sleepFor(plan, date).wake) + offset),
    ),
    untimed: false,
    anchor: { type: 'wake', offsetMin: offset },
  });
  const anytime: Timing = { startTime: '00:00', untimed: true, anchor: { type: 'anytime' } };

  // Pass 1: everything with its own time (clock, any time, getting up, going to bed).
  const followers: ScheduleItem[] = [];
  for (const item of input.items) {
    const type = item.anchorType ?? 'time';
    if (type === 'after') {
      followers.push(item);
      continue;
    }
    const offset = item.anchorOffsetMin ?? 0;
    for (const originDate of itemDates(item, from, to, input.plan)) {
      emit(item, originDate, (date) => {
        if (type === 'anytime') return anytime;
        if (type === 'wake') return wakeAt(date, offset);
        if (type === 'bed') {
          const sleep = sleepFor(plan, date);
          const wake = timeToMinutes(sleep.wake);
          let bed = timeToMinutes(sleep.bed);
          if (bed < wake) bed += 24 * 60; // after midnight: still "tonight"
          return {
            startTime: minutesToTime(Math.max(0, Math.min(24 * 60 - 1, bed - offset))),
            untimed: false,
            anchor: { type: 'bed', offsetMin: offset },
          };
        }
        return { startTime: item.startTime, untimed: false };
      });
    }
  }

  // Pass 2: "po aktivnosti" – after that activity's occurrence on the same day.
  const timedOwn = [...out];
  for (const item of followers) {
    const offset = item.anchorOffsetMin ?? 0;
    const series = item.anchorItemId
      ? (seriesOf.get(item.anchorItemId) ?? item.anchorItemId)
      : null;
    const anchorTitle =
      input.items.find((i) => i.id === item.anchorItemId)?.title ??
      (series ? input.items.find((i) => (i.seriesId ?? i.id) === series)?.title : undefined) ??
      'aktivnost';
    for (const originDate of itemDates(item, from, to, input.plan)) {
      emit(item, originDate, (date) => {
        const anchor = series
          ? timedOwn.find((o) => o.date === date && !o.untimed && seriesOf.get(o.itemId) === series)
          : undefined;
        if (anchor) {
          const at = DateTime.fromISO(anchor.end, { zone: input.zone }).plus({ minutes: offset });
          const sameDay = at.toISODate() === date;
          return {
            startTime: sameDay ? at.toFormat('HH:mm') : '23:59',
            untimed: false,
            anchor: {
              type: 'after',
              offsetMin: offset,
              title: anchor.title,
              occKey: anchor.key,
              anchorEnd: anchor.end,
            },
          };
        }
        const fallback = item.anchorFallback ?? 'skip';
        if (fallback === 'skip') return null;
        const base = fallback === 'wake' ? wakeAt(date, 0) : anytime;
        return {
          ...base,
          anchor: {
            type: 'after',
            offsetMin: offset,
            title: anchorTitle,
            occKey: null,
            anchorEnd: null,
          },
        };
      });
    }
  }

  return out.sort((a, b) => a.start.localeCompare(b.start) || a.title.localeCompare(b.title, 'sl'));
}

export interface ArrangedDay {
  /** Running right now (start ≤ now < end). */
  current: Occurrence[];
  /** The next one to start, when nothing else is running. */
  next: Occurrence | null;
  /** Everything after `next`/`current`. */
  later: Occurrence[];
  /** Already started/finished, most recent first. */
  earlier: Occurrence[];
}

/** Orders a day for the "Danes" screen: what matters now comes first. */
export function arrangeDay(occurrences: readonly Occurrence[], now: Date): ArrangedDay {
  const t = now.toISOString();
  const current: Occurrence[] = [];
  const upcoming: Occurrence[] = [];
  const earlier: Occurrence[] = [];
  for (const o of occurrences) {
    if (o.start > t) upcoming.push(o);
    else if (o.end > t) current.push(o);
    else earlier.push(o);
  }
  earlier.reverse();
  if (current.length > 0) return { current, next: null, later: upcoming, earlier };
  const [next = null, ...later] = upcoming;
  return { current, next, later, earlier };
}

/** Minutes of travel around an occurrence (0 when none; tolerant of older cached data). */
export const travelOf = (o: Partial<Pick<Occurrence, 'travelBeforeMin' | 'travelAfterMin'>>) => ({
  before: o.travelBeforeMin ?? 0,
  after: o.travelAfterMin ?? 0,
});

/** When to leave: start minus travel there (null without travel). */
export function departureTime(
  o: Pick<Occurrence, 'startTime'> & Partial<Pick<Occurrence, 'travelBeforeMin'>>,
): LocalTime | null {
  const before = o.travelBeforeMin ?? 0;
  if (before <= 0) return null;
  return minutesToTime((timeToMinutes(o.startTime) - before + 24 * 60) % (24 * 60));
}

const shortLength = (minutes: number) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h === 0 ? `${m} min` : m === 0 ? `${h} h` : `${h} h ${m} min`;
};

/** "po Fitnes", "30 min po vstajanju", "pred spanjem", "kadarkoli" (null for a clock time). */
export function anchorLabel(o: Pick<Occurrence, 'anchor' | 'untimed'>): string | null {
  const a = o.anchor;
  if (!a) return o.untimed ? 'kadarkoli' : null;
  switch (a.type) {
    case 'anytime':
      return 'kadarkoli';
    case 'wake':
      return a.offsetMin > 0 ? `${shortLength(a.offsetMin)} po vstajanju` : 'ob vstajanju';
    case 'bed':
      return a.offsetMin > 0 ? `${shortLength(a.offsetMin)} pred spanjem` : 'pred spanjem';
    case 'after':
      // A day without that activity: the fallback (any time / at wake-up).
      if (!a.occKey) return o.untimed ? 'kadarkoli' : 'ob vstajanju';
      return a.offsetMin > 0 ? `${shortLength(a.offsetMin)} po: ${a.title}` : `po: ${a.title}`;
  }
}
